/**
 * Minimal SSE client for POST /api/chat. No libs — fetch + ReadableStream +
 * TextDecoder. Returns an async iterable of `ChatEvent`s so the caller can
 * render tokens + retrieval metadata as they arrive.
 *
 * Auth:
 *   - If MSAL real-auth is enabled (VITE_USE_REAL_AUTH=true), attach
 *     `Authorization: Bearer <token>` acquired via msal.acquireToken.
 *   - Otherwise attach the `X-Dev-User` header from the picker.
 */
import { acquireToken, USE_REAL_AUTH } from "../auth/msal";
import { noteRequestId } from "../error-reporting";
import type { ChatEvent } from "../types/chat";

export class ChatError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    /** Correlation id of the failed request, when the server sent one.
     * Surfaced to the user so they can quote it in a bug report and an
     * operator can paste it straight into the admin Logs tab. */
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ChatError";
  }
}

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export interface SendOptions {
  message: string;
  /** Dev-mode identity. Sent as `X-Dev-User`. Ignored when real auth is on. */
  devUser?: string;
  /** Optional assistant slug (e.g. "compliance"). Backend picks default if omitted. */
  assistantSlug?: string;
  /** Continue an existing conversation; omit to start a new one. */
  conversationId?: number;
  /** KB-picker selection for this turn. Sent so a KB chosen before the
   *  conversation exists (first message) scopes the first answer. `undefined`
   *  = don't send (backend falls back to the persisted selection); `[]` =
   *  explicit clear. */
  kbIds?: number[];
  /** AbortSignal to cancel the stream mid-flight. */
  signal?: AbortSignal;
  /** ADR 0060 phase 5 — what the user has on screen when chatting from an
   *  assistant's own page. Appended to the system prompt for this turn. */
  pageContext?: string;
}

export interface StartedChat {
  /** Async stream of server events. */
  events: AsyncGenerator<ChatEvent, void, void>;
  /** Conversation id the backend is streaming into. */
  conversationId: number | null;
}

async function authHeaders(): Promise<Record<string, string>> {
  if (!USE_REAL_AUTH) return {};
  const acquired = await acquireToken();
  if (!acquired) return {};
  return { Authorization: `Bearer ${acquired.token}` };
}

/**
 * Fire the /api/chat request, return the async iterable + the conversation id
 * the server picked (read from the X-Conversation-Id response header).
 */
/** Mirrors `MAX_MESSAGE_CHARS` on the backend's ChatRequest. The composer
 * counts against it so the refusal happens before the request, not after. */
export const MAX_MESSAGE_CHARS = 20_000;

export async function sendChat(opts: SendOptions): Promise<StartedChat> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(await authHeaders()),
  };
  if (!USE_REAL_AUTH && opts.devUser) headers["X-Dev-User"] = opts.devUser;

  const body: Record<string, unknown> = { message: opts.message };
  if (opts.assistantSlug) body.assistant_slug = opts.assistantSlug;
  if (opts.conversationId) body.conversation_id = opts.conversationId;
  if (opts.kbIds !== undefined) body.kb_ids = opts.kbIds;
  if (opts.pageContext) body.page_context = opts.pageContext;

  const res = await fetch(`${API_BASE}/api/chat`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal: opts.signal,
  });
  // Capture the correlation id BEFORE the error branch — a failed chat
  // turn is exactly when someone needs to quote one. It lets an operator
  // paste it into the admin Logs tab and see every record from that
  // request across services (ADR 0041).
  const requestId = res.headers.get("X-Request-Id") ?? undefined;
  noteRequestId(requestId);

  if (!res.ok) {
    const errBody = await res.text().catch(() => "");
    // FastAPI errors are `{"detail": "..."}`. Show that sentence on its own
    // when we can parse it: a budget refusal (429) already explains itself
    // — which limit, how much of it is gone, when it clears — and wrapping
    // that in `HTTP 429: {"detail":...}` buries the one line the user needs.
    let detail = "";
    try {
      const parsed = JSON.parse(errBody);
      if (typeof parsed?.detail === "string") detail = parsed.detail;
      // 428 onboarding_required carries a structured detail; its sentence is
      // the one to show while the app brings up the video.
      else if (typeof parsed?.detail?.message === "string") detail = parsed.detail.message;
      // Pydantic's 422 is a list of field errors that echoes the whole
      // input; for a too-long message that is the message itself, sliced
      // mid-sentence. Say the one thing the person can act on instead.
      else if (res.status === 422 && Array.isArray(parsed?.detail)) {
        const tooLong = parsed.detail.find(
          (d: { type?: string; loc?: unknown[] }) =>
            d?.type === "string_too_long" && d?.loc?.includes("message"),
        );
        if (tooLong) {
          detail = `The message is too long (max ${MAX_MESSAGE_CHARS.toLocaleString("da-DK")} characters). Attach long text as a file instead.`;
        }
      }
    } catch {
      // Not JSON (a proxy's HTML error page, say) — fall through to the raw body.
    }
    throw new ChatError(
      detail || `HTTP ${res.status}: ${errBody.slice(0, 200)}`,
      res.status,
      requestId,
    );
  }
  if (!res.body) {
    throw new ChatError("response has no body", res.status, requestId);
  }

  const headerConvId = res.headers.get("X-Conversation-Id");
  const conversationId =
    headerConvId && /^\d+$/.test(headerConvId) ? Number(headerConvId) : null;

  return { events: iterateSse(res.body), conversationId };
}

async function* iterateSse(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<ChatEvent, void, void> {
  const reader = body.getReader();
  const dec = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += dec.decode(value, { stream: true });

      // SSE frames are separated by a blank line (`\n\n`).
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const evt = parseFrame(frame);
        if (evt) yield evt;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseFrame(frame: string): ChatEvent | null {
  // A frame may have multiple lines; we only use `data:`.
  const payload = frame
    .split("\n")
    .filter((l) => l.startsWith("data:"))
    .map((l) => l.slice(5).trimStart())
    .join("");
  if (!payload || payload === "[DONE]") return null;
  try {
    return JSON.parse(payload) as ChatEvent;
  } catch {
    return null;
  }
}

/** Lightweight type returned by /api/health. */
export interface HealthResponse {
  status: string;
  version: string;
  chat_model: string;
  embedding_model: string;
  search_index: string;
  rbac_resolver: string;
  dev_auth_enabled: boolean;
  require_auth?: boolean;
  jwt_enabled?: boolean;
}

export async function fetchHealth(): Promise<HealthResponse | null> {
  try {
    const r = await fetch(`${API_BASE}/api/health`, { cache: "no-store" });
    noteRequestId(r.headers.get("X-Request-Id"));
    if (!r.ok) return null;
    return (await r.json()) as HealthResponse;
  } catch {
    return null;
  }
}
